// assert.ts
import {
  isArray,
  isBigInt,
  isBoolean,
  isDeepEqual,
  isFunction,
  isNonEmptyArray,
  isNonEmptyString,
  isNoRef,
  isNull,
  isNumber,
  isObject,
  isObjectOrArray,
  isRef,
  isString,
  isSymbol,
  isUndefined,
  isUUIDString
} from "./guard.mjs";
import { AssertionError, typeName } from "./types.mjs";
function assertionFailure(what, type, message) {
  const description = message ? `${message} (was: ${typeName(what)})` : `Argument of type ${typeName(what)} is not ${type}`;
  throw new AssertionError(description);
}
function assert(what, message) {
  what || assertionFailure(what, "truthy", message);
}
function fail(message = "Assertion failure") {
  throw new AssertionError(message);
}
function assertRef(what, message) {
  isRef(what) || assertionFailure(what, "referenceable", message);
}
function assertNoRef(what, message) {
  isNoRef(what) || assertionFailure(what, "null or undefined", message);
}
function assertUndefined(what, message) {
  isUndefined(what) || assertionFailure(what, "undefined", message);
}
function assertNull(what, message) {
  isNull(what) || assertionFailure(what, "null", message);
}
function assertBoolean(what, message) {
  isBoolean(what) || assertionFailure(what, "a boolean", message);
}
function assertNumber(what, message) {
  isNumber(what) || assertionFailure(what, "a number", message);
}
function assertBigInt(what, message) {
  isBigInt(what) || assertionFailure(what, "a bigint", message);
}
function assertString(what, message) {
  isString(what) || assertionFailure(what, "a string", message);
}
function assertSymbol(what, message) {
  isSymbol(what) || assertionFailure(what, "a symbol", message);
}
function assertFunction(what, message) {
  isFunction(what) || assertionFailure(what, "a function", message);
}
function assertObject(what, message) {
  isObject(what) || assertionFailure(what, "an object", message);
}
function assertArray(what, message) {
  isArray(what) || assertionFailure(what, "an array", message);
}
function assertObjectOrArray(what, message) {
  isObjectOrArray(what) || assertionFailure(what, "an object or array", message);
}
function assertType(what, type, message) {
  if (typeof type === "function") {
    what instanceof type || assertionFailure(what, `an instance of "${type.name}"`, message);
    return;
  }
  switch (type) {
    case "undefined":
      assertUndefined(what, message);
      return;
    case "null":
      assertNull(what, message);
      return;
    case "boolean":
      assertBoolean(what, message);
      return;
    case "number":
      assertNumber(what, message);
      return;
    case "bigint":
      assertBigInt(what, message);
      return;
    case "string":
      assertString(what, message);
      return;
    case "symbol":
      assertSymbol(what, message);
      return;
    case "function":
      assertFunction(what, message);
      return;
    case "object":
      assertObject(what, message);
      return;
    case "array":
      assertArray(what, message);
      return;
  }
  const argument = typeof type === "string" ? `"${type}"` : typeName(type);
  throw new AssertionError(`Invalid type specified for assertion: ${argument}`);
}
function assertNonEmptyArray(what, message) {
  isNonEmptyArray(what) || assertionFailure(what, "a non-empty array", message);
}
function assertNonEmptyString(what, message) {
  isNonEmptyString(what) || assertionFailure(what, "a non-empty string", message);
}
function assertUUIDString(what, message) {
  isUUIDString(what) || assertionFailure(what, "a UUID string", message);
}
function assertArrayOf(what, type, message) {
  assertArray(what, message);
  what.forEach((item, index) => {
    try {
      assertType(item, type, message);
    } catch (error) {
      const message2 = error?.message || "Unknown error";
      throw new AssertionError(`${message2} [index=${index}]`);
    }
  });
}
function assertNonEmptyArrayOf(what, type, message) {
  assertNonEmptyArray(what, message);
  assertArrayOf(what, type, message);
}
function assertDeepEqual(what, other, message) {
  isDeepEqual(what, other) || assertionFailure(what, `deep equal to ${typeName(other)}`, message);
}
export {
  assert,
  assertArray,
  assertArrayOf,
  assertBigInt,
  assertBoolean,
  assertDeepEqual,
  assertFunction,
  assertNoRef,
  assertNonEmptyArray,
  assertNonEmptyArrayOf,
  assertNonEmptyString,
  assertNull,
  assertNumber,
  assertObject,
  assertObjectOrArray,
  assertRef,
  assertString,
  assertSymbol,
  assertType,
  assertUUIDString,
  assertUndefined,
  fail
};
//# sourceMappingURL=assert.mjs.map
